import { useIntl } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import PresenceActiveIcon from "@atlaskit/icon/glyph/presence-active";

import { useAdg4TabsContext } from "@/components/adg4/tabs/src";
import { useUserContext } from "@/components/user-context/user-context-provider";
import { Link } from "@/libraries/design/components";
import { useLocalizedHref } from "@/libraries/web-framework";

import { tabbedHeroSectionDatabasesMessages } from "../messages/tabbed-hero-section.messages";

import { ContentTypeVideo } from "./content-type-video";

import type { TabType, TabProps } from "./tab-type";

type DatabasesContentTypeProps = TabProps & {
    isConfluenceCrossflow?: boolean;
};

export const DatabasesContentType: TabType = ({
    index,
    isConfluenceCrossflow
}: DatabasesContentTypeProps) => {
    const { isCurrentTab } = useAdg4TabsContext();
    const intl = useIntl();
    const ctaLink = useLocalizedHref("/software/confluence/databases");
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { user } = useUserContext();

    return (
        <ContentTypeVideo
            active={isCurrentTab(index)}
            title={intl.formatMessage(
                tabbedHeroSectionDatabasesMessages.contentTitle
            )}
            description={intl.formatMessage(
                tabbedHeroSectionDatabasesMessages.contentDescription
            )}
            iconTitle={intl.formatMessage(
                tabbedHeroSectionDatabasesMessages.contentIconTitle
            )}
            iconCollection={[
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionDatabasesMessages.contentFeaturePoint1
                    ),
                    icon: <PresenceActiveIcon size="small" label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionDatabasesMessages.contentFeaturePoint2
                    ),
                    icon: <PresenceActiveIcon size="small" label="" />
                },
                {
                    description: intl.formatMessage(
                        tabbedHeroSectionDatabasesMessages.contentFeaturePoint3
                    ),
                    icon: <PresenceActiveIcon size="small" label="" />
                }
            ]}
            cta={
                <Link
                    href={ctaLink}
                    text={intl.formatMessage(
                        tabbedHeroSectionDatabasesMessages.contentCta
                    )}
                    type={"primary"}
                    dataEventProps={
                        !isConfluenceCrossflow
                            ? {
                                  "data-product-key": "confluence",
                                  "data-event-component": "linkButton",
                                  "data-event-container": "contentTypesSection",
                                  "data-event": "clicked",
                                  "data-label": "explore-databases",
                                  "data-label-english": "Explore Databases",
                                  "data-track-event":
                                      "confluence-pt.content-types.databases.cta"
                              }
                            : undefined
                    }
                    onClick={() => {
                        if (!isConfluenceCrossflow) {
                            return;
                        }
                        createAnalyticsEvent({
                            eventName: "confluence-crossflow-tab-cta-clicked",
                            eventType: "ui",
                            action: "clicked",
                            actionSubject: "linkButton",
                            actionSubjectId: "confluenceCrossFlowHeroSection",
                            attributes: {
                                reactComponent: "DatabasesContentType",
                                tab: "databases",
                                userTeamType: user.extended_profile?.team_type
                            }
                        }).fire();
                    }}
                />
            }
            reverseLayout
            videoSrcMp4="https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Databases_LowBR_reencode.mp4"
            videoSrcWebm="https://wac-cdn.atlassian.com/misc-assets/videos/confluence-product-tour/PT_CT_Databases_LowBR.webm"
            videoPreviewSrc="https://wac-cdn.atlassian.com/misc-assets/webp-images/confluence/PT_CT_Databases_Preview.webp"
        />
    );
};
